import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import bcrypt from 'bcryptjs';
import { supabase } from '../lib/supabase';

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
}

interface AuthContextType {
  user: AdminUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    const token = localStorage.getItem('admin_token');
    if (token) {
      const verifiedUser = verifyToken(token);
      if (verifiedUser) {
        setUser(verifiedUser);
      } else {
        localStorage.removeItem('admin_token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    // eslint-disable-next-line no-useless-catch
    try {
      // Validate input
      if (!email || !password) {
        throw new Error("Email y contraseña son requeridos");
      }

      const trimmedEmail = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        throw new Error("Formato de email inválido");
      }

      // Find user in admin_users table
      const { data: adminUser, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', trimmedEmail)
        .eq('is_active', true)
        .single();

      if (error || !adminUser) {
        throw new Error("Credenciales inválidas");
      }

      // Verify password with bcrypt
      const passwordMatch = await bcrypt.compare(password, adminUser.password_hash);
      if (!passwordMatch) {
        throw new Error("Credenciales inválidas");
      }

      // Create session token
      const sessionToken = btoa(JSON.stringify({
        id: adminUser.id,
        email: adminUser.email,
        role: adminUser.role,
        iat: Math.floor(Date.now() / 1000),
      }));

      const userData: AdminUser = {
        id: adminUser.id,
        email: adminUser.email,
        full_name: adminUser.full_name,
        role: adminUser.role,
        is_active: adminUser.is_active,
      };

      setUser(userData);
      localStorage.setItem('admin_token', sessionToken);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('admin_token');
  };

  const verifyToken = (token: string): AdminUser | null => {
    try {
      const decoded = JSON.parse(atob(token));

      // Verify token is not too old (24 hours)
      const now = Math.floor(Date.now() / 1000);
      if (now - decoded.iat > 86400) {
        return null;
      }

      // Return user data from token (we trust the token for session management)
      return {
        id: decoded.id,
        email: decoded.email,
        full_name: decoded.full_name || 'Admin',
        role: decoded.role,
        is_active: true,
      };
    } catch {
      return null;
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};